import React, { useState, useRef, useEffect, useReducer } from 'react';
import classNames from 'classnames';

import { Content } from 'src/common/interfaces/content.interface';
import LoadingSpinner from 'src/common/components/loading-spinner';
import { MediaItem } from './media-item';
import { apiGetAllContents } from 'src/utils/journeyApi';
import { useCurrentOrganization } from 'src/store/organization';
import { MediaSearchInput } from './search-input';
import { useScroll } from 'react-use';
import { NeueLoadingSpinner } from '../components/loading/spinner';
import { NeueJourneyLoadingSpinner } from '../components/loading/journey-spinner';

type Props = {
  onSelectContent: (content: Content) => void;
  updateKey: number;
};

export const JourneyMediaPanel = ({ onSelectContent, updateKey }: Props) => {
  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);
  const [contents, setContents] = useState<Content[]>([]);
  const [searchStr, setSearchStr] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasMoreRef = useRef(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const isSearchResultEmpty = searchStr !== '' && contents.length === 0;

  // check if scroll reached the bottom with 40px offset
  const isScrollAtBottom = scrollRef.current
    ? scrollRef.current?.scrollHeight - scrollRef.current?.scrollTop - scrollRef.current?.clientHeight < 40
    : false;

  const loadNextPage = () => {
    if (!loading) {
      setPage(page + 1);
    }
  };

  const getAllContents = (searchStr: string = '', page: number = 1) => {
    return apiGetAllContents(currentOrganization.id, {
      sort_by: 'updated_at',
      sort_order: 'desc',
      is_media: true,
      search_str: searchStr,
      page,
    });
  };

  useScroll(scrollRef);

  useEffect(() => {
    if (isScrollAtBottom && hasMoreRef.current) {
      loadNextPage();
    }
  }, [isScrollAtBottom]);

  useEffect(() => {
    setLoading(true);
    getAllContents(searchStr, page).then((response) => {
      const { page, pages, contents: responseContents } = response;
      if (page === 1) {
        setContents(responseContents);
      } else {
        setContents([...contents, ...responseContents]);
      }
      hasMoreRef.current = page < pages;
      setLoading(false);
    });
  }, [currentOrganization.id, searchStr, page]);

  const onSearch = (searchStr: string) => {
    setSearchStr(searchStr);
    setPage(1);
  };

  return (
    <>
      <MediaSearchInput onSearch={onSearch} />
      <div
        className={classNames('h-[422px] overflow-y-auto', {
          'flex items-center justify-center': loading,
        })}
        ref={scrollRef}
      >
        {isSearchResultEmpty ? (
          <span className='text-neue-journey-medium'>No content found</span>
        ) : contents.length === 0 && !loading ? (
          <span className='text-neue-journey-medium'>
            Link, images, or files used in Journey they will appear here for easy reuse
          </span>
        ) : (
          <div className='flex flex-col'>
            {contents.map((content: Content) => (
              <MediaItem
                updateKey={updateKey}
                key={content.id}
                content={content}
                onSelect={() => onSelectContent(content)}
              />
            ))}
          </div>
        )}
        {loading && <NeueLoadingSpinner theme='journey' size='16' className={classNames({ 'h-full': page === 1 })} />}
      </div>
    </>
  );
};
